import * as React from 'react';
import { useTheme } from '@emotion/react';
import { camelCase, startCase } from 'lodash';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useGetTaskHistoryQuery,
  useRequestServicesMutation
} from '../../generated/graphql';
import { useServiceAlert } from '../../utils';
import { useServiceList } from '../Dashboard';
import { useAuth, useServices } from '../../store';

const useHotelServices = () => {
  const theme: any = useTheme();
  const guest = useAuth((state: any) => state.guest);
  const [requestServices, { loading: requestingServices }] =
    useRequestServicesMutation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const service = searchParams.get('service');
  const [currentService, setCurrentService] = React.useState(service);
  const currentServiceSimplified = startCase(
    camelCase(currentService as string)
  );

  // Use open service list
  const { data: taskHistoryData, loading: loadingTaskHistory } =
    useGetTaskHistoryQuery({
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-and-network',
      variables: {
        guestID: guest?.guest_id,
        propertyID: guest?.property_id,
        roomID: guest?.room_id
      },
      skip: !guest?.guest_id
    });

  const {
    servicesList,
    groupedServices,
    loading: loadingServiceList
  } = useServiceList();
  const currentServiceList =
    groupedServices[currentServiceSimplified as string];

  // Service Request Flow
  const services = useServices((state: any) => Object.values(state.services));
  const itemsInCart = React.useMemo(
    () =>
      services.reduce((prev: number, current: any) => prev + current?.count, 0),
    [services]
  );

  const cancelServices = useServices((state: any) => state.cancelServices);
  const addToService = useServices((state: any) => state.addToService);
  const removeFromService = useServices(
    (state: any) => state.removeFromService
  );
  const serviceAlert = useServiceAlert(() => {
    navigate(-1);
    cancelServices();
  });

  const submitServices = async () => {
    const inputServices = services.map((service: any) => {
      return {
        property_id: guest?.property_id,
        quantity: service?.count,
        room_id: guest?.room_id,
        task_id: service?.id
      };
    });

    try {
      const res = await requestServices({
        variables: {
          input: [...inputServices]
        }
      });
      if (res.data?.requestServices?.code === '200') {
        serviceAlert('Submitted Successfully');
      } else {
        serviceAlert(
          res.data?.requestServices?.message ||
            'Something went wrong! Please contact support'
        );
      }
    } catch (err) {
      console.log(err);
      serviceAlert(`Something went wrong! Please try again`);
    }
  };
  // Input Search
  const [searchInput, setSearchInput] = React.useState('');

  const filteredServiceList = React.useMemo(() => {
    return currentServiceList?.filter(({ task }: any) =>
      task.toLowerCase().includes(searchInput.toLowerCase())
    );
  }, [currentServiceList, searchInput]);

  const taskHistoryMap = React.useMemo(() => {
    const taskHistoryMap: any = {};
    taskHistoryData?.getTaskHistory?.forEach((task: any) => {
      taskHistoryMap[task?.task_list] = task;
    });
    return taskHistoryMap;
  }, [taskHistoryData?.getTaskHistory]);

  return {
    theme,
    navigate,
    itemsInCart,
    searchInput,
    servicesList,
    addToService,
    cancelServices,
    submitServices,
    setSearchInput,
    taskHistoryMap,
    removeFromService,
    setCurrentService,
    currentServiceSimplified,
    currentServiceList: filteredServiceList,
    loading: loadingServiceList || requestingServices || loadingTaskHistory
  };
};

export default useHotelServices;
